import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Modal from './common/Modal';

const { FiSave, FiFolder, FiType } = FiIcons;

const ProjectForm = ({ project, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    color: '#3b82f6' // Default blue color
  });

  const [errors, setErrors] = useState({});

  // Predefined color options
  const colorOptions = [
    { name: 'blue', value: '#3b82f6' },
    { name: 'purple', value: '#8b5cf6' },
    { name: 'pink', value: '#ec4899' },
    { name: 'red', value: '#ef4444' },
    { name: 'orange', value: '#f97316' },
    { name: 'yellow', value: '#eab308' },
    { name: 'green', value: '#22c55e' },
    { name: 'teal', value: '#14b8a6' },
    { name: 'slate', value: '#64748b' },
  ];

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        color: project.color || '#3b82f6'
      });
    }
  }, [project]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('projects.projectNameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const projectData = {
      ...formData,
      name: formData.name.trim()
    };

    if (project) {
      onSubmit({ ...project, ...projectData });
    } else {
      onSubmit(projectData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={project ? t('projects.editProject') : t('projects.createProject')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <SafeIcon icon={FiType} />
              {t('projects.projectName')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('projects.projectNamePlaceholder')}
              className={`w-full px-4 py-3 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-800 dark:text-white transition-colors ${
                errors.name ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 dark:border-slate-600'
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Project Color */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <SafeIcon icon={FiFolder} />
              {t('projects.projectColor')}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {colorOptions.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => handleChange('color', color.value)}
                  className={`h-10 rounded-lg border-2 transition-all hover:scale-105 active:scale-95 ${
                    formData.color === color.value
                      ? 'border-slate-800 dark:border-white scale-110'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={t(`projects.colors.${color.name}`)}
                  aria-label={t(`projects.colors.${color.name}`)}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-xl">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{t('projects.preview')}</p>
            <div
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-600"
              style={{ borderLeft: `4px solid ${formData.color}` }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: formData.color }}
              >
                <SafeIcon icon={FiFolder} className="text-white" />
              </div>
              <span className="font-medium text-slate-800 dark:text-white">
                {formData.name || t('projects.projectName')}
              </span>
            </div>
          </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
        >
          <SafeIcon icon={FiSave} />
          {project ? t('projects.updateProject') : t('projects.createProject')}
        </button>
      </form>
    </Modal>
  );
};

export default ProjectForm;